import React, { useMemo } from 'react';
import usePersistedKeyValueStore from '../../hooks/usePersistedKeyValueStore';
import { Color, colors } from '../../utils/theme';
import { hexToRgbA } from '../../utils/hexToRGBA';
import Stack, { ContentStack } from './Stack';
import Text from './Text';
import IconButton from './IconButton';
import Divider from './Divider';
import MainContentContainer, { mainContentPadding } from './MainContentContainer';
import { AlertIcon, IconName } from './Icon';
import Link from './Link';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { StyleProp, ViewStyle } from 'react-native';

export type ActionItem = {
    title: string;
    onPress: () => void;
    isDisabled?: boolean;
    isLoadingDisabled?: boolean;
    testID?: string;
};

export type AlertType = 'info' | 'error' | 'warning' | 'success';

export type AlertOwnProps = {
    type: AlertType;
    title: string;
    message?: string | React.ReactNode;
    isDismissible?: boolean;
    persistedDismissKey?: string;
    actionItems?: ActionItem[];
    content?: React.ReactNode;
    justifyActionItems?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
    testID?: string;
    style?: StyleProp<ViewStyle>;
};

export type AlertProps = AlertOwnProps;

const colorMap: Record<AlertType, Color> = {
    info: 'Primary',
    error: 'Alert',
    warning: 'Primary-Reg',
    success: 'Success',
};

const iconTypeMap: Record<AlertType, IconName> = {
    info: 'Lightbulb',
    error: 'AlertCircle',
    warning: 'AlertTriangle',
    success: 'Check',
};

const Alert: React.FC<AlertProps> = ({
    type,
    title,
    message,
    isDismissible = false,
    persistedDismissKey,
    actionItems,
    content,
    justifyActionItems,
    testID,
    style,
}) => {
    const isMobile = useIsMobileLayout();
    const {
        value: persistedIsDismissed,
        setValue: setPersistedIsDismissed,
        isLoading,
    } = usePersistedKeyValueStore({
        category: 'alert-dismissal',
        key: persistedDismissKey || 'no-key',
        defaultValue: false,
    });
    const handleDismiss = () => {
        if (persistedDismissKey) {
            setPersistedIsDismissed(true);
        }
    };

    const backgroundColor = useMemo(() => {
        const colorVariant = colorMap[type];
        const hex = colors[colorVariant];
        return hexToRgbA(hex, 0.1);
    }, [type]);

    if ((persistedIsDismissed && isDismissible) || (isDismissible && isLoading)) return null;

    return (
        <Stack horizontal fullWidth justify="center" style={style}>
            <Stack
                vertical
                fullWidth
                style={{
                    backgroundColor,
                    borderRadius: 5,
                    maxWidth: isMobile ? undefined : 400,
                }}
                testID={testID}
            >
                <Stack
                    horizontal
                    justify="space-between"
                    align="center"
                    fullWidth
                    style={{ padding: mainContentPadding }}
                >
                    <Stack horizontal align="center" justify="flex-start" space={2.5} fullWidth>
                        <AlertIcon size="xs" variant={type} name={iconTypeMap[type]} />
                        <Text variant="h5" style={{ flex: 1 }}>
                            {title}
                        </Text>
                    </Stack>
                    {isDismissible ? <IconButton variant="minimal" icon="X" onPress={handleDismiss} /> : null}
                </Stack>
                <Divider horizontal />
                <MainContentContainer>
                    <ContentStack>
                        {message ? <Text variant="body-1">{message}</Text> : null}
                        {content || null}
                        {actionItems?.length ? (
                            <Stack horizontal justify={justifyActionItems || 'flex-start'} align="center" fullWidth>
                                {actionItems.map((item, index) => (
                                    <Link
                                        key={index + item.title}
                                        onPress={item.onPress}
                                        isDisabled={item.isDisabled}
                                        testID={item.testID}
                                        variant="body-1"
                                    >
                                        {item.title}
                                    </Link>
                                ))}
                            </Stack>
                        ) : null}
                    </ContentStack>
                </MainContentContainer>
            </Stack>
        </Stack>
    );
};

export default React.memo(Alert);
